/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import ErrorPage from 'next/error'
import React, { useEffect } from 'react'
import { CardProps } from 'src/common/components/Card'
import { ChallengeDetailContent } from 'src/common/components/Challenge/ChallengeDetailContent'
import { ChallengeDetailHero } from 'src/common/components/Challenge/ChallengeDetailHero'
import { Lane } from 'src/common/components/Lane'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { RadixTabs } from 'src/common/components/Tabs/RadixTabs'
import { trackView } from 'src/common/lib/segment'
import { ViewEventProps } from 'src/common/lib/segment/types'
import { prependAssetHost, prependAssetHostChallenge } from 'src/common/utils'
import { mapSkillLevelString } from 'src/common/utils/challenges'
import { getEventById, getRelatedChallenges } from 'src/services/mainAPI'
import { JoinEventRes } from 'src/services/types/joinEventV2'
import { ILoginUser } from 'src/services/types/user'
import { wrapper } from 'src/store'

type ChallengeDetailProps = {
    item: JoinEventRes
    relatedChallenges: any
    error: any
    user?: ILoginUser
}

const ChallengeDetail = ({
    item,
    relatedChallenges,
    error,
    user
}: ChallengeDetailProps) => {
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const eventProps: ViewEventProps = {
                productData: item,
                user: user
            }
            trackView(eventProps)
        }
    })

    if (error) {
        return <ErrorPage statusCode={404} />
    }

    const tabs =
        item !== null
            ? [
                  {
                      label: 'About',
                      value: 'about',
                      content: (
                          <>
                              <ChallengeDetailContent
                                  hasAccess={item.summary.hasAccess}
                                  hasJoined={item.cart.challengeAthleteId !== null}
                                  distanceUnit={item.summary.distanceUnit}
                                  totalDistance={item.summary.totalDistance}
                                  title={item.summary.name}
                                  htmlString={item.summary.fundraisingMessage?.replace(
                                      /(<|&lt;)br\s*\/*(>|&gt;)/g,
                                      ''
                                  )}
                              />
                              <section className="pl-2 md:pl-0">
                                  <Lane
                                      title="Related Challenges"
                                      data={
                                          relatedChallenges.challengeDiscovery?.groups[0]?.challenges.map(
                                              (item): CardProps => {
                                                  return {
                                                      cardType: 'challenge',
                                                      //   badgeType: getChallengeBadgeType(
                                                      //       item,
                                                      //       user
                                                      //   ),
                                                      duration: `${item.totalDistance} ${item.distanceUnit}`,
                                                      imageURL: item.logo
                                                          ? prependAssetHostChallenge(
                                                                item.logo
                                                            )
                                                          : prependAssetHost(
                                                                '/icons/no-challenge-bg.png'
                                                            ),
                                                      excerciseType: '',
                                                      title: item.name,
                                                      trainers: [
                                                          {
                                                              intensity:
                                                                  mapSkillLevelString(
                                                                      item.skillLevelString
                                                                  ),
                                                              name: item.skillLevelString
                                                          }
                                                      ],
                                                      href: `/challenges/${encodeURIComponent(
                                                          item.challengeUrl
                                                      )}`
                                                  }
                                              }
                                          ) ?? []
                                      }
                                  />
                              </section>
                          </>
                      )
                  }
              ]
            : []
    return (
        <AppLayout
            pageTitle={item?.summary?.name}
            metaDescription={item?.summary?.metaDescription}
            metaKeywords={item?.summary?.metaKeywords}
            metaImage={prependAssetHostChallenge(item.summary.imageUrl)}
            metaTitle={`${item?.summary?.name} | Virtual Fitness Challenges | Yes.Fit`}
        >
            <ChallengeDetailHero
                video={
                    item.summary.introVideoUrl
                        ? {
                              videoThumbUrl: prependAssetHostChallenge(
                                  item.summary.imageUrl
                              ),
                              videoUrl: item.summary.introVideoUrl
                          }
                        : null
                }
                mainImage={
                    item.summary.imageUrl
                        ? prependAssetHostChallenge(item.summary.imageUrl)
                        : prependAssetHostChallenge(item.summary.logo)
                }
                challengeUrl={item.summary.challengeUrl}
            />
            <div className="mx-auto max-w-screen-fullHd">
                <RadixTabs tabs={tabs} />
            </div>
        </AppLayout>
    )
}

export const getServerSideProps = wrapper.getServerSideProps(
    (store) => async (context) => {
        const { id } = context.params
        const user = store.getState().user.data ?? null
        const challengeUrl = id as string
        const { data: item, error } = await store.dispatch(
            getEventById.initiate(challengeUrl)
        )

        const { data: relatedChallenges } = await store.dispatch(
            getRelatedChallenges.initiate(item?.summary?.challengeId ?? null)
        )

        return {
            props: {
                item: item ?? null,
                user: user,
                relatedChallenges,
                error: error ?? null
            }
        }
    }
)

export default ChallengeDetail
